import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getStatusChipSx } from '../../constants/statusColors';
import {
  Typography,
  Box,
  Button,
  Avatar,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { csAdminApi } from '../../services/api';
import { useGlobalPermission } from '../../hooks/useGlobalPermission';
import { mapDirectorFromApi } from '../../utils/apiMappers';
import { exportDirectorProfileToCsv } from '../../utils/directorExport';
import DeleteConfirmDialog from '../../components/common/DeleteConfirmDialog';

// Import tab panels (we'll create these)
import OverviewTab from './tabs/OverviewTab';
import DocumentsTab from './tabs/DocumentsTab';
import CommitteesTab from './tabs/CommitteesTab';
import CompaniesTab from './tabs/CompaniesTab';
import ActivityTab from './tabs/ActivityTab';
import AuditLogTab from './tabs/AuditLogTab';

const DirectorDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [director, setDirector] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [documentCount, setDocumentCount] = useState(null);
  const { allowed: canDeleteDirector, loading: deletePermLoading } = useGlobalPermission(
    'directors',
    'delete'
  );

  useEffect(() => {
    fetchDirectorDetails();
  }, [id]);

  const fetchDirectorDetails = async () => {
    try {
      setLoading(true);

      const response = await csAdminApi.getDirectorById(id);
      const row = response.data?.data;
      if (!row) {
        setError('Director not found');
        setDirector(null);
        return;
      }
      setDirector(mapDirectorFromApi(row));
      setDocumentCount(Array.isArray(row.documents) ? row.documents.length : null);
      setError('');
    } catch (err) {
      console.error('Error fetching director details:', err);
      setError('Failed to load director details');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExport = () => {
    handleMenuClose();
    if (!director) return;
    exportDirectorProfileToCsv(director);
    enqueueSnackbar('Director profile exported', { variant: 'success' });
  };

  const handleDelete = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setActionLoading(true);
      await csAdminApi.deleteDirector(id);
      enqueueSnackbar('Director deleted', { variant: 'success' });
      navigate('/directors');
    } catch (err) {
      console.error('Failed to delete director:', err);
      const message = err.response?.data?.error?.message || 'Failed to delete director';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setActionLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !director) {
    return (
      <Box sx={{ px: 3, py: 3 }}>
        <Alert severity="error">{error || 'Director not found'}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/directors')}
          sx={{ mt: 2 }}
        >
          Back to Directors
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3, py: 3 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/directors')}
        sx={{ mb: 2 }}
      >
        Back to Directors
      </Button>

      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{ 
              width: 80, 
              height: 80,
              bgcolor: '#D32F2F',
              fontSize: '1.75rem',
              fontWeight: 600,
            }}
            src={director.avatar}
          >
            {director.firstName?.[0]}{director.lastName?.[0]}
          </Avatar>
          <Box>
            <Typography variant="h4">
              {director.firstName} {director.lastName}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {director.position}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {director.companyName}
            </Typography>
            <Chip
              label={director.status}
              sx={{
                ...getStatusChipSx(director.status),
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 24,
                borderRadius: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mt: 1,
              }}
              size="small"
            />
          </Box>
        </Box>

        <Box>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/directors/${id}/edit`)}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <IconButton onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="OVERVIEW" />
          <Tab label={`DOCUMENTS (${documentCount ?? director.documents?.length ?? 0})`} />
          <Tab label={`COMMITTEES (${director.committees?.length || 0})`} />
          <Tab label={`COMPANIES (${director.companies?.length || director.companyCount || 0})`} />
          <Tab label="ACTIVITY" />
          <Tab label="AUDIT LOG" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      {activeTab === 0 && <OverviewTab director={director} />}
      {activeTab === 1 && (
        <DocumentsTab
          directorId={id}
          documents={director.documents}
          companyId={director.companyId}
          onRefresh={fetchDirectorDetails}
          onDocumentsChange={setDocumentCount}
        />
      )}
      {activeTab === 2 && <CommitteesTab committees={director.committees} />}
      {activeTab === 3 && (
        <CompaniesTab
          companies={director.companies}
          currentDirectorId={director.id}
          onRefresh={fetchDirectorDetails}
        />
      )}
      {activeTab === 4 && <ActivityTab directorId={id} activities={director.activities} />}
      {activeTab === 5 && <AuditLogTab directorId={id} auditLogs={director.auditLogs} />}

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleExport}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export Profile</ListItemText>
        </MenuItem>
        {!deletePermLoading && canDeleteDirector && (
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }} disabled={actionLoading}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete Director</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Director"
        entityName={`${director.firstName} ${director.lastName}`}
        entityType="director"
        impacts={[
          'Permanently remove director record',
          'Delete linked user account',
          'Remove position and detail records',
          'Cannot be undone',
        ]}
        loading={actionLoading}
      />
    </Box>
  );
};

export default DirectorDetails;
